import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Kanban, List, Pencil, Plus } from "lucide-react";
import { CopyIframeButton, LeadEditDialog } from "@/components/LeadEditDialog";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, StatusBadge } from "@/components/shared";
import { LeadFields } from "@/components/LeadFields";
import { api, errMsg, fdate, inr, label, useApi } from "@/lib/api";

const STAGES = ["NEW_INQUIRY", "CONTACTED", "REQUIREMENT_GATHERED", "QUOTATION_SENT", "NEGOTIATION", "WON_APPROVED", "LOST"];

function LeadCard({ l }) {
  return (
    <Link to={`/leads/${l.id}`} className="block rounded-md border bg-card p-3 hover:border-foreground/25 transition-colors" data-testid={`lead-card-${l.leadNumber}`}>
      <div className="flex justify-between text-[11px] text-muted-foreground"><span className="font-mono">{l.leadNumber}</span><span>{l.source}</span></div>
      <div className="font-medium text-sm mt-1.5">{l.clientName}</div>
      <div className="text-xs text-muted-foreground">{l.clientCity}</div>
      <div className="flex justify-between mt-3 text-xs"><span className="num">{inr(l.budget)}</span><span className="text-muted-foreground">{l.quoteCount} quote{l.quoteCount === 1 ? "" : "s"}</span></div>
    </Link>
  );
}

export default function Leads() {
  const { data, reload } = useApi("/leads");
  const [view, setView] = useState("list");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const nav = useNavigate();
  const create = async (f) => {
    try {
      const r = await api.post("/leads", f);
      toast.success(`Lead ${r.data.leadNumber} created`);
      setOpen(false);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Leads" subtitle="Track every inquiry from first contact to approved build.">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            <TabsTrigger value="pipeline" data-testid="leads-view-pipeline"><Kanban className="h-4 w-4 mr-1.5" />Pipeline</TabsTrigger>
            <TabsTrigger value="list" data-testid="leads-view-list"><List className="h-4 w-4 mr-1.5" />List</TabsTrigger>
          </TabsList>
        </Tabs>
        <CopyIframeButton />
        <Button onClick={() => setOpen(true)} data-testid="new-lead-button"><Plus className="h-4 w-4 mr-2" />New lead</Button>
      </PageHeader>
      {view === "pipeline" ? (
        <div className="flex gap-3 overflow-x-auto pb-4 snap-x" data-testid="leads-pipeline">
          {STAGES.map((s) => {
            const items = (data || []).filter((l) => l.status === s);
            return (
              <div key={s} className="w-[78vw] sm:w-64 shrink-0 snap-start rounded-lg bg-muted/40 border p-2.5">
                <div className="flex items-center justify-between px-1 pb-2.5"><span className="text-xs font-semibold">{label(s)}</span><span className="text-xs text-muted-foreground num">{items.length}</span></div>
                <div className="space-y-2 min-h-[80px]">{items.map((l) => <LeadCard key={l.id} l={l} />)}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-lg border bg-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead><tr className="border-b bg-muted/40 text-xs text-muted-foreground">
              {["Lead", "Client", "City", "Source", "Budget", "Quotes", "Owner", "Status", "Created", ""].map((h) => <th key={h} className="text-left font-medium px-4 py-2.5">{h}</th>)}
            </tr></thead>
            <tbody>
              {data && !data.length && <tr><td colSpan={10} className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="leads-empty">No leads yet. Add one or share the public lead form.</td></tr>}
              {(data || []).map((l) => (
                <tr key={l.id} onClick={() => nav(`/leads/${l.id}`)} className="border-b last:border-0 cursor-pointer hover:bg-secondary/30" data-testid={`lead-row-${l.leadNumber}`}>
                  <td className="px-4 py-3 font-mono text-xs">{l.leadNumber}</td>
                  <td className="px-4 py-3"><div className="font-medium">{l.clientName}</div><div className="text-xs text-muted-foreground">{l.clientPhone}</div></td>
                  <td className="px-4 py-3">{l.clientCity}</td><td className="px-4 py-3">{l.source}</td>
                  <td className="px-4 py-3 num">{inr(l.budget)}</td><td className="px-4 py-3 num">{l.quoteCount}</td>
                  <td className="px-4 py-3 text-xs" data-testid={`lead-owner-${l.leadNumber}`}>{l.assignedSalesName || <span className="text-muted-foreground">Unassigned</span>}</td>
                  <td className="px-4 py-3"><StatusBadge status={l.status} /></td><td className="px-4 py-3 text-xs text-muted-foreground">{fdate(l.createdAt)}</td>
                  <td className="px-4 py-3"><Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditing(l); }} data-testid={`edit-lead-${l.leadNumber}`}><Pencil className="h-3.5 w-3.5" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LeadEditDialog lead={editing} onClose={() => setEditing(null)} onSaved={reload} />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-lead-dialog">
          <DialogHeader><DialogTitle>New lead</DialogTitle></DialogHeader>
          <LeadFields onSubmit={create} submitText="Create lead" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
